import { difference, featureCollection, getCoords, polygon } from '@turf/turf';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Feature, MultiPolygon, Polygon, Position } from 'geojson';

import { Coordinates3D } from '@/types/commons/commons.types';
import {
  InitializationMaskingZone,
  NoFlyZone,
  NoFlyZone3d,
  NoInitZone,
  PolygonWithAltitude,
  SolidPolygon3d,
  Zone,
  Zone2DTypeEnum,
  Zone3DTypeEnum,
  ZoneFormTypeEnum,
  ZoneTypeEnum,
  ZoneWithMargin,
} from '@/types/config/config.types';
import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { FeatureTypeEnum } from '@/types/map.types';

export const computeZoneMargin = (zwm: ZoneWithMargin): number[][][] => {
  try {
    const marginCoordinates =
      'type' in zwm.margin ? zwm.margin.coordinates : [zwm.margin.map((value) => toLonLatArray(value, false)!)];
    const polygonCoordinates =
      'type' in zwm.polygon ? zwm.polygon.coordinates : [zwm.polygon.map((value) => toLonLatArray(value, false)!)];
    const zonePolygon = polygon(polygonCoordinates);
    const marginPolygon = polygon(marginCoordinates);
    return getCoords(difference(featureCollection([marginPolygon, zonePolygon])) || []);
  } catch (_e) {
    //TODO https://dev.azure.com/GroupeADP/D1969-HOLOGARDE/_workitems/edit/20398
    return [];
  }
};

export const isNoFlyZone3d = (zone: Zone): zone is NoFlyZone3d => {
  return zone.type === ZoneTypeEnum.NFZ_3D;
};

export const isNoFlyZone = (zone: Zone): zone is NoFlyZone => {
  return zone.type === ZoneTypeEnum.NFZ;
};

export const isNoFlyZoneType = (zoneType: ZoneFormTypeEnum): boolean => {
  return [
    Zone2DTypeEnum.NFZ_INFO,
    Zone2DTypeEnum.NFZ_WARNING,
    Zone2DTypeEnum.NFZ_CRITICAL,
    Zone3DTypeEnum.NFZ_INFO_3D,
    Zone3DTypeEnum.NFZ_WARNING_3D,
    Zone3DTypeEnum.NFZ_CRITICAL_3D,
  ].includes(zoneType);
};

export const isNoInitZone = (zone: Zone): zone is NoInitZone => {
  return zone.type === ZoneTypeEnum.NIZ;
};

export const isInitializationMaskingZone = (zone: Zone): zone is InitializationMaskingZone => {
  return zone.type === ZoneTypeEnum.IMZ;
};

export const isZoneFeature = (type: FeatureTypeEnum): boolean => {
  return [
    FeatureTypeEnum.NO_FLY_ZONE,
    FeatureTypeEnum.NO_INIT_ZONE,
    FeatureTypeEnum.NO_FLY_ZONE_3D,
    FeatureTypeEnum.INITIALIZATION_MASKING_ZONE,
    FeatureTypeEnum.INITIALIZATION_MASKING_ZONE_MARGIN,
  ].includes(type);
};

export const filterMapping = {
  [Zone2DTypeEnum.NFZ_INFO]: (zone: Feature) =>
    zone.properties?.value.type === ZoneTypeEnum.NFZ && zone.properties.value.level === DroneAlertLevelEnum.DRONE_INFO,
  [Zone2DTypeEnum.NFZ_WARNING]: (zone: Feature) =>
    zone.properties?.value.type === ZoneTypeEnum.NFZ &&
    zone.properties.value.level === DroneAlertLevelEnum.DRONE_WARNING,
  [Zone2DTypeEnum.NFZ_CRITICAL]: (zone: Feature) =>
    zone.properties?.value.type === ZoneTypeEnum.NFZ &&
    zone.properties.value.level === DroneAlertLevelEnum.DRONE_CRITICAL,

  [Zone3DTypeEnum.NFZ_INFO_3D]: (zone: Feature) =>
    zone.properties?.value.type === ZoneTypeEnum.NFZ_3D &&
    zone.properties.value.level === DroneAlertLevelEnum.DRONE_INFO,
  [Zone3DTypeEnum.NFZ_WARNING_3D]: (zone: Feature) =>
    zone.properties?.value.type === ZoneTypeEnum.NFZ_3D &&
    zone.properties.value.level === DroneAlertLevelEnum.DRONE_WARNING,
  [Zone3DTypeEnum.NFZ_CRITICAL_3D]: (zone: Feature) =>
    zone.properties?.value.type === ZoneTypeEnum.NFZ_3D &&
    zone.properties.value.level === DroneAlertLevelEnum.DRONE_CRITICAL,

  [Zone2DTypeEnum.NIZ]: (zone: Feature) => zone.properties?.value.type === ZoneTypeEnum.NIZ,
  [Zone2DTypeEnum.IMZ]: (zone: Feature) => zone.properties?.value.type === ZoneTypeEnum.IMZ,
};

export const filterFeaturesByZoneType = (features: Feature[], zoneType: ZoneFormTypeEnum): Feature[] => {
  const filterFunction = filterMapping[zoneType];
  return features.filter(filterFunction);
};

export const getNoFlyZoneCriticality = (nfz: NoFlyZone | NoFlyZone3d) => {
  switch (nfz.level) {
    case DroneAlertLevelEnum.DRONE_CRITICAL:
      return 3;
    case DroneAlertLevelEnum.DRONE_WARNING:
      return 2;
    case DroneAlertLevelEnum.DRONE_INFO:
      return 1;
  }
};

function pointToString(p: Coordinates3D): string {
  return `${p.longitude},${p.latitude},${p.altitude}`;
}

function positionToString(p: Position): string {
  return `${p[0]},${p[1]},${p[2]}`;
}

function createEdgeKey(p1: Position | Coordinates3D, p2: Position | Coordinates3D): string {
  const point1 = 'type' in p1 ? pointToString(p1) : positionToString(p1);
  const point2 = 'type' in p2 ? pointToString(p2) : positionToString(p2);
  // Ensure consistent ordering
  return point1 < point2 ? `${point1}|${point2}` : `${point2}|${point1}`;
}

function isSolidClosed(faces: Position[][][] | PolygonWithAltitude[]): boolean {
  const edgeSet: Set<string> = new Set();

  for (const face of faces.map((f) =>
    'polygon' in f ? f.polygon.slice(0, f.polygon.length - 1) : f[0].slice(0, f[0].length - 1),
  )) {
    for (let i = 0; i < face.length; i++) {
      const p1 = face[i];
      const p2 = face[(i + 1) % face.length]; // Wrap around to form a loop

      const edge = createEdgeKey(p1, p2);

      if (edgeSet.has(edge)) {
        edgeSet.delete(edge);
      } else {
        edgeSet.add(edge);
      }
    }
  }

  // If all edges are paired, the set should be empty
  return edgeSet.size === 0;
}

export function isSolid3dPolygonValid(solidPolygon: MultiPolygon | SolidPolygon3d): boolean {
  return isSolidClosed('type' in solidPolygon ? solidPolygon.coordinates : solidPolygon.polygonFaces);
}

export function getAllPoints2dOfSolidPolygon(solidPolygon: MultiPolygon | SolidPolygon3d): Position[] {
  if ('type' in solidPolygon) {
    return solidPolygon.coordinates
      .map((face) => face[0])
      .flatMap((polygon) => polygon.map((position) => [position[0], position[1]]));
  }

  return solidPolygon.polygonFaces
    .map((face) => face.polygon)
    .flatMap((face) => face.map((point3d) => [point3d.longitude, point3d.latitude]));
}

// Utilise l'algorithme de Chan, de complexité n*log(h) (n = nb de points initial, h = nb de points du résultat final)
// Retourne la figure la plus petite qui contient tous les points de la projection, ce qui enlève les points inutiles
// qui se retrouvent au milieu de la figure après la projection au sol pour garder que les bordures
export function computeConvexHull(points: Position[]): Position[] {
  if (points.length <= 1) return points;

  // Sorting points lexicographically
  points.sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

  const cross = (o: Position, a: Position, b: Position): number => {
    return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  };

  const hull: Position[] = [];
  // Build the lower hull
  for (let i = 0; i < points.length; i++) {
    while (hull.length >= 2 && cross(hull[hull.length - 2], hull[hull.length - 1], points[i]) <= 0) {
      hull.pop();
    }
    hull.push(points[i]);
  }

  // Build the upper hull
  const t = hull.length + 1;
  for (let i = points.length - 1; i >= 0; i--) {
    while (hull.length >= t && cross(hull[hull.length - 2], hull[hull.length - 1], points[i]) <= 0) {
      hull.pop();
    }
    hull.push(points[i]);
  }

  return hull;
}

export function getProjectionOf3dSolid(solidPolygon: MultiPolygon | SolidPolygon3d): Polygon {
  // Le solide est déjà passé par isSolid3dPolygonValid dans des sélecteurs précédents
  const allPoints2d = getAllPoints2dOfSolidPolygon(solidPolygon);
  return { type: 'Polygon', coordinates: [computeConvexHull(allPoints2d)] };
}
